import { useEffect } from "react";

declare global {
  interface Window {
    __assetChainConsoleErrors__?: Array<{
      type: "error" | "rejection";
      message: string;
      stack?: string;
      timestamp: number;
    }>;
  }
}

/**
 * Development helper that captures runtime errors/unhandled rejections
 * so we can paste them into log/logIssue.md.
 */
export function ConsoleErrorTracker() {
  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const store = (window.__assetChainConsoleErrors__ = window.__assetChainConsoleErrors__ || []);

    const handleError = (event: ErrorEvent) => {
      const payload = {
        type: "error" as const,
        message: event.message,
        stack: event.error?.stack,
        timestamp: Date.now(),
      };
      store.push(payload);
      console.error("[Tracked Error]", payload.message, event.error);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason instanceof Error ? event.reason : new Error(String(event.reason));
      const payload = {
        type: "rejection" as const,
        message: reason.message,
        stack: reason.stack,
        timestamp: Date.now(),
      };
      store.push(payload);
      console.error("[Tracked Rejection]", payload.message, reason);
    };

    window.addEventListener("error", handleError);
    window.addEventListener("unhandledrejection", handleRejection);

    return () => {
      window.removeEventListener("error", handleError);
      window.removeEventListener("unhandledrejection", handleRejection);
    };
  }, []);

  return null;
}
